import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  BsCameraVideo, BsMegaphone, BsGlobe, BsBroadcast, BsFileText,
  BsPlayCircle, BsCalendarEvent, BsArrowRight,
} from 'react-icons/bs';
import { MdPermMedia } from "react-icons/md";

import { Link } from 'react-router-dom';
import dashboardService from '../api/dashboardService';
import videoService from '../api/videoService';
import posterService from '../api/posterService';
import StarDivider from '../components/uiComponents/StarDivider';
import { VideoPreview } from '../components/VideoPlayers';

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function formatDate(raw) {
  if (!raw) return '—';
  return new Date(raw).toISOString().slice(0, 10);
}

/** Skeleton rows shown while loading */
function SkeletonRows({ count = 5 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px',
          borderBottom: i < count - 1 ? '1px solid #faf5f5' : 'none',
        }}>
          <div style={{ width: 32, height: 32, borderRadius: 7, backgroundColor: '#f0e8e8', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 10, borderRadius: 4, backgroundColor: '#f0e8e8', width: '70%', marginBottom: 5 }} />
            <div style={{ height: 8, borderRadius: 4, backgroundColor: '#f5f0f0', width: '45%' }} />
          </div>
          <div style={{ width: 40, height: 10, borderRadius: 4, backgroundColor: '#f0e8e8' }} />
        </div>
      ))}
    </>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', flexDirection: 'column', gap: 8, padding: 20 }}>
      <Icon size={32} />
      <span style={{ fontSize: 12 }}>{label}</span>
    </div>
  );
}

const STAT_CARDS = [
  { key: 'videos', label: 'Vidéos', icon: BsCameraVideo, subKey: 'actif', subLabel: 'actives', color: '#8B0000', bg: '#fce8e8', link: '/videos' },
  { key: 'posters', label: 'Affiches', icon: BsMegaphone, subKey: 'prevu', subLabel: 'prévues', color: '#1a5276', bg: '#d6eaf8', link: '/affiches' },
  { key: 'representations', label: 'Représentations', icon: BsGlobe, subKey: null, subLabel: null, color: '#1e8449', bg: '#d5f5e3', link: '/representations' },
  { key: 'channels', label: 'Canaux', icon: BsBroadcast, subKey: null, subLabel: null, color: '#7d3c98', bg: '#f4ecf7', link: '/canaux' },
  { key: 'media', label: 'Médiathèque', icon: MdPermMedia, subKey: null, subLabel: null, color: '#b7770d', bg: '#fef9e7', link: '/media' },
];

const TYPE_BADGE = {
  'Événements': { bg: '#fce8e8', color: '#8B0000' },
  'Campagnes': { bg: '#d5f5e3', color: '#1e8449' },
  'Annonces': { bg: '#d6eaf8', color: '#1a5276' },
};

function StatusBadge({ status }) {
  const active = status === 'actif';
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: 0.5, padding: '2px 8px',
      borderRadius: 20, textTransform: 'uppercase',
      backgroundColor: active ? '#d5f5e3' : '#f5f5f5',
      color: active ? '#1e8449' : '#999',
    }}>
      {status}
    </span>
  );
}

function Panel({ title, icon: Icon, linkTo, children, height = '100%' }) {
  return (
    <div style={{
      backgroundColor: '#fff', border: '1px solid #f0e0e0',
      borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
      display: 'flex', flexDirection: 'column', height,
    }}>
      <div style={{
        padding: '13px 18px', borderBottom: '1px solid #f5f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={15} color="#8B0000" />
          <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.4 }}>{title}</span>
        </div>
        <Link to={linkTo} style={{ fontSize: 11, color: '#8B0000', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
          Voir tout <BsArrowRight size={11} />
        </Link>
      </div>
      <div className="hide-scrollbar" style={{ overflowY: 'auto', flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);

  const [affiches, setAffiches] = useState([]);
  const [affichesLoading, setAffichesLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  /* Fetch stats */
  useEffect(() => {
    dashboardService.getStats()
      .then(({ data }) => setStats(data.data))
      .catch(() => setStatsError('Impossible de charger les statistiques.'))
      .finally(() => setStatsLoading(false));
  }, []);

  /* Fetch recent videos (latest 5, sorted desc by date) */
  useEffect(() => {
    videoService.list({ page: 1, limit: 5 })
      .then(({ data }) => setVideos(data.data || []))
      .catch(() => setVideos([]))
      .finally(() => setVideosLoading(false));
  }, []);

  /* Fetch upcoming affiches (next 4) */
  useEffect(() => {
    posterService.list({ page: 1, limit: 4 })
      .then(({ data }) => setAffiches(data.data || []))
      .catch(() => setAffiches([]))
      .finally(() => setAffichesLoading(false));
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Latest video = first video */
  const latestVideo = videos[0] ?? null;

  const displayedVideos = isMobile ? videos.slice(0, 5) : videos;
  const displayedAffiches = isMobile ? affiches.slice(0, 4) : affiches;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : 'calc(100vh - 140px)' }}>
      {/* ── Header ── */}
      <div className="text-center mb-4">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>TABLEAU DE BORD</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* ── Stat cards ── */}
      {statsLoading && <div className="text-center py-4 text-secondary" style={{ fontSize: 13 }}>Chargement…</div>}
      {statsError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{statsError}</div>}

      {stats && (
        <Row className="g-3 mb-4">
          {STAT_CARDS.map(({ key, label, icon: Icon, subKey, subLabel, color, bg, link }) => {
            const entry = stats[key] || {};
            const cardContent = (
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #ece8e8',
                borderRadius: 14,
                padding: '18px 20px 16px',
                boxShadow: '0 2px 8px rgba(139,0,0,0.06)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                cursor: link ? 'pointer' : 'default',
                transition: 'box-shadow 0.15s, transform 0.15s',
              }}
                onMouseEnter={e => { if (link) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(139,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(139,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  backgroundColor: bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={18} color={color} />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                  {entry.total ?? 0}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
                  {subKey && (
                    <div style={{ fontSize: 11, color, marginTop: 2, fontWeight: 600 }}>
                      {entry[subKey] ?? 0} {subLabel}
                    </div>
                  )}
                </div>
              </div>
            );
            return (
              <Col key={key} xs={6} sm={4} lg={true}>
                {link ? <Link to={link} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>{cardContent}</Link> : cardContent}
              </Col>
            );
          })}
        </Row>
      )}

      {/* ── Bottom panels ── */}
      <Row className="g-3" style={isMobile ? {} : { flex: 1, minHeight: 0 }}>

        {/* Latest video preview */}
        <Col xs={12} lg={4} style={isMobile ? {} : { height: '100%' }}>
          <div style={{
            backgroundColor: '#fff', border: '1px solid #f0e0e0',
            borderRadius: 10, overflow: 'hidden',
            boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
            display: 'flex', flexDirection: 'column',
            height: isMobile ? 'auto' : '100%', minHeight: 0,
          }}>
            <div style={{
              padding: '13px 18px', borderBottom: '1px solid #f5f0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BsPlayCircle size={16} color="#8B0000" />
                <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.4 }}>Dernière vidéo</span>
              </div>
              <Link to="/videos" style={{ fontSize: 11, color: '#8B0000', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                Voir tout <BsArrowRight size={11} />
              </Link>
            </div>
            {videosLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', padding: 16 }}>
                  <div style={{ height: 160, borderRadius: 6, backgroundColor: '#f0e8e8', marginBottom: 10 }} />
                  <div style={{ height: 12, borderRadius: 4, backgroundColor: '#f0e8e8', width: '80%', marginBottom: 6 }} />
                  <div style={{ height: 10, borderRadius: 4, backgroundColor: '#f5f0f0', width: '55%' }} />
                </div>
              </div>
            ) : latestVideo ? (
              <div style={{ display: 'flex', flexDirection: 'column', flex: isMobile ? 'none' : 1, minHeight: 0 }}>
                <div style={{
                  height: isMobile ? 220 : undefined,
                  flex: isMobile ? 'none' : 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  background: '#000',
                }}>
                  <VideoPreview video={latestVideo} dashboardMode />
                </div>
                <div style={{ padding: '10px 16px', borderTop: '1px solid #f5f0f0', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {latestVideo.theme}
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{latestVideo.intervenant}</span>
                    <span>{formatDate(latestVideo.date)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState icon={BsPlayCircle} label="Aucune vidéo disponible" />
            )}
          </div>
        </Col>

        {/* Recent videos list */}
        <Col xs={12} lg={4} style={isMobile ? {} : { height: '100%' }}>
          <Panel title="Vidéos récentes" icon={BsPlayCircle} linkTo="/videos" height="100%">
            {videosLoading ? (
              <SkeletonRows count={5} />
            ) : displayedVideos.length === 0 ? (
              <EmptyState icon={BsCameraVideo} label="Aucune vidéo récente" />
            ) : (
              displayedVideos.map((v, i) => (
                <div key={v.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px',
                  borderBottom: i < displayedVideos.length - 1 ? '1px solid #faf5f5' : 'none',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 7, backgroundColor: '#fce8e8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#8B0000', flexShrink: 0,
                  }}>
                    <BsCameraVideo size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.theme}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{v.intervenant}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <StatusBadge status={v.statut} />
                    <div style={{ fontSize: 10, color: '#ccc', marginTop: 2 }}>{formatDate(v.date)}</div>
                  </div>
                </div>
              ))
            )}
          </Panel>
        </Col>

        {/* Upcoming affiches */}
        <Col xs={12} lg={4} style={isMobile ? {} : { height: '100%' }}>
          <Panel title="Affiches à venir" icon={BsCalendarEvent} linkTo="/affiches" height="100%">
            {affichesLoading ? (
              <SkeletonRows count={4} />
            ) : displayedAffiches.length === 0 ? (
              <EmptyState icon={BsCalendarEvent} label="Aucune affiche à venir" />
            ) : (
              displayedAffiches.map((a, i) => {
                const badge = TYPE_BADGE[a.type_affiche] || { bg: '#f5f5f5', color: '#666' };
                return (
                  <div key={a.id} style={{
                    padding: '11px 16px',
                    borderBottom: i < displayedAffiches.length - 1 ? '1px solid #faf5f5' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.titre_evenement}
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{a.pays_concernes}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: badge.bg, color: badge.color }}>
                          {a.type_affiche}
                        </span>
                        <div style={{ fontSize: 10, color: '#ccc', marginTop: 2 }}>{formatDate(a.date_evenement)}</div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>
        </Col>

      </Row>
    </div>
  );
}
